import { apiRequest } from './api';

// ── Types ───────────────────────────────────────────────────

export type TransportAllocationPayload = {
    student_id: string;
    academic_session_id: string;
    transport_route_id: string;
    // Stop is optional — a student can be assigned to a route without a specific stop.
    route_stop_id?: string | null;
    custom_monthly_fee?: number | null;
    start_date: string;
    end_date?: string | null;
    is_active?: boolean;
};

export type TransportAllocationRecord = {
    id: string;
    organization_id: string;
    student_id: string;
    academic_session_id: string;
    transport_route_id: string | null;
    route_stop_id: string | null;
    custom_monthly_fee: number | null;
    // Backend-resolved fee (custom → stop → route default), as a decimal string.
    effective_monthly_fee?: string | number | null;
    start_date: string;
    end_date: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    student?: {
        id: string;
        enrollment_number?: string | null;
        first_name: string;
        last_name: string | null;
        name?: string;
        father_name?: string | null;
        mother_name?: string | null;
        father_contact_number?: string | null;
        address?: string | null;
        current_class?: { id: string; name: string } | null;
    };
    // Present for stop-less allocations (and safe to include alongside route_stop).
    transport_route?: {
        id: string;
        title: string;
        default_monthly_fee?: string | number | null;
    } | null;
    route_stop?: {
        id: string;
        monthly_fee: string;
        stop: { id: string; name: string };
        route: { id: string; title: string };
    } | null;
    academic_session?: {
        id: string;
        name: string;
    };
};

// ── API Functions ───────────────────────────────────────────

export async function fetchTransportAllocations(params?: {
    transportRouteId?: string;
    perPage?: number;
}): Promise<TransportAllocationRecord[]> {
    const query = new URLSearchParams();
    if (params?.transportRouteId) query.set('transport_route_id', params.transportRouteId);
    if (params?.perPage) query.set('per_page', String(params.perPage));

    const qs = query.toString();
    const res = await apiRequest<TransportAllocationRecord[]>(`/api/transport-allocations${qs ? `?${qs}` : ''}`);
    return res.data;
}

export async function createTransportAllocation(
    payload: TransportAllocationPayload
): Promise<TransportAllocationRecord> {
    const res = await apiRequest<TransportAllocationRecord>('/api/transport-allocations', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
    return res.data;
}
